/**
 * DOM overlay typography composition per SPEC §4.6.
 *
 * The on-screen overlay is rendered as absolutely-positioned <div>s in the
 * #overlay element so the type stays pixel-sharp at any DPR. The Capture
 * Agent renders text into a 2D canvas at export resolution separately.
 *
 * Wave 4 integrator: add the typography stylesheet to index.html via
 *   <link rel="stylesheet" href="/src/typography/typography.css">
 * Do NOT import the CSS from this module — keep it externalised so the CSS
 * graph for the UI Agent's styles stays untouched.
 *
 * Callers that intend to export a PNG should `await ensureFontsReady()`
 * before triggering the offscreen render so glyphs are rasterised with the
 * final webfont rather than the system fallback.
 */
import type { Corner, TextLayout, TitleOrientation } from '../types.js';

/**
 * Mirror values for the CSS custom properties defined in
 * typography.css. Kept in sync manually — if you change one, change
 * the other. The 2D-canvas path uses these directly (em → px is just
 * `value × titlePx`); the DOM overlay path lets CSS read them.
 *
 * 要調整 — the vertical value is an opening guess, not a final taste call.
 */
export const LETTER_SPACING_TITLE_HORIZONTAL_EM = -0.02;
export const LETTER_SPACING_TITLE_VERTICAL_EM = 0.05;

export interface ComposeContext {
  /** The #overlay element (absolutely covers the canvas, pointer-events: none). */
  overlay: HTMLElement;
  /** Result of pickCorners(seed, aspect). */
  layout: TextLayout;
  /** Title text — e.g. "カナワツギ". */
  title: string;
  /** Date string — e.g. "2026.05.21 Thu. – 05.27 Wed.". */
  date: string;
  /** Time string — e.g. "OPEN 12:00–20:00 [Wed. until 16:00]". */
  time: string;
  /**
   * Short edge of the canvas in CSS pixels. Used to scale font sizes so type
   * reads consistently across aspects. If omitted, the smaller dimension of
   * the overlay's bounding rect is used (falling back to window short edge).
   */
  canvasShortEdge?: number;
  /**
   * Optional fine-tuning multiplier on the computed font sizes. Defaults to 1.
   * Reserved for Wave 4 visual tuning.
   */
  scale?: number;
  /**
   * Optional override for the title font-size in CSS pixels. When provided,
   * the title element is sized to this value verbatim (the auto-computed
   * shortEdge/14 default is bypassed). The mono caption sizes still derive
   * from the auto title unit so caption proportions stay stable.
   */
  titleSizePx?: number;
  /**
   * Optional CSS font-weight for the title (e.g. 100..900). Defaults to the
   * stylesheet's 500.
   */
  titleWeight?: number;
  /**
   * Title axis. 'vertical' adds the .kw-text--title-vertical class
   * (writing-mode: vertical-rl) on the title <div>. Metadata is
   * always horizontal regardless. Default: 'horizontal'.
   */
  orientation?: TitleOrientation;
}

/** Safe-zone padding from the canvas edge (SPEC §4.6: 32px). */
const SAFE_ZONE_PX = 32;

/**
 * Base unit divisor: title ≈ canvasShortEdge / 14 ≈ 77px @ 1080-tall canvas.
 * Mono ≈ unit * 0.18 ≈ 14px — caption-class against the 32px safe-zone.
 *
 * This is a tighter scale than the literal "1.0 unit / 0.18 unit" notation in
 * SPEC §4.6 so the type doesn't overpower the blob composition; the spec
 * itself describes those numbers as relative units that need calibration.
 */
const TITLE_UNIT_DIVISOR = 14;
const MONO_UNIT_RATIO = 0.18;

function resolveShortEdge(ctx: ComposeContext): number {
  if (typeof ctx.canvasShortEdge === 'number' && ctx.canvasShortEdge > 0) {
    return ctx.canvasShortEdge;
  }
  const rect = ctx.overlay.getBoundingClientRect();
  const overlayShort = Math.min(rect.width, rect.height);
  if (overlayShort > 0) return overlayShort;
  if (typeof window !== 'undefined') {
    return Math.min(window.innerWidth, window.innerHeight);
  }
  return 1080; // sensible default
}

function cornerClass(corner: Corner): string {
  return `kw-text--${corner}`;
}

function makeTitleDiv(
  text: string,
  corner: Corner,
  fontPx: number,
  fontWeight?: number,
  orientation: TitleOrientation = 'horizontal',
): HTMLDivElement {
  const el = document.createElement('div');
  const verticalCls = orientation === 'vertical' ? ' kw-text--title-vertical' : '';
  el.className = `kw-text kw-text--title${verticalCls} ${cornerClass(corner)}`;
  el.setAttribute('aria-hidden', 'true');
  el.style.fontSize = `${fontPx}px`;
  if (typeof fontWeight === 'number' && Number.isFinite(fontWeight)) {
    el.style.fontWeight = String(Math.round(fontWeight));
  }
  el.style.pointerEvents = 'none';
  // Vertical orientation collapses `\n` to a single ASCII space so the
  // column stays a single run — multi-column wrapping is out of scope
  // for the current Phase 8 layout (see drawVerticalText comment in
  // the 2D canvas path). Horizontal orientation preserves `\n` and
  // CSS `.kw-text--title { white-space: pre-line }` renders them as
  // hard breaks.
  el.textContent = orientation === 'vertical'
    ? text.replace(/\n/g, ' ')
    : text;
  return el;
}

/** Two-line metadata block (Phase 6): Date on line 1, Time on line 2. */
function makeMetadataDiv(
  date: string,
  time: string,
  corner: Corner,
  fontPx: number,
): HTMLDivElement {
  const el = document.createElement('div');
  el.className = `kw-text kw-text--mono ${cornerClass(corner)}`;
  el.setAttribute('aria-hidden', 'true');
  el.style.fontSize = `${fontPx}px`;
  el.style.pointerEvents = 'none';
  // Two lines separated by a <br>; CSS line-height controls the gap.
  el.appendChild(document.createTextNode(date));
  el.appendChild(document.createElement('br'));
  el.appendChild(document.createTextNode(time));
  return el;
}

/**
 * Render (or re-render) the typography overlay. Idempotent — fully replaces
 * any previous overlay contents on each call.
 */
export function composeTypography(ctx: ComposeContext): void {
  const shortEdge = resolveShortEdge(ctx);
  const scale = typeof ctx.scale === 'number' && ctx.scale > 0 ? ctx.scale : 1;
  const titleUnit = (shortEdge / TITLE_UNIT_DIVISOR) * scale;
  const titlePx =
    typeof ctx.titleSizePx === 'number' && ctx.titleSizePx > 0
      ? ctx.titleSizePx
      : titleUnit;
  const monoPx = titleUnit * MONO_UNIT_RATIO;

  const titleEl = makeTitleDiv(
    ctx.title,
    ctx.layout.title.corner,
    titlePx,
    ctx.titleWeight,
    ctx.orientation,
  );
  const metaEl = makeMetadataDiv(
    ctx.date,
    ctx.time,
    ctx.layout.metadata.corner,
    monoPx,
  );

  // Remove only the typography elements (.kw-text); any unrelated overlay
  // children (e.g. future heads-up indicators) persist across re-layouts.
  const stale = ctx.overlay.querySelectorAll(':scope > .kw-text');
  for (const el of stale) el.remove();
  ctx.overlay.append(titleEl, metaEl);
}

/** Remove all typography children from the overlay. */
export function clearTypography(overlay: HTMLElement): void {
  overlay.replaceChildren();
}

/**
 * Resolve once webfonts (Zen Kaku Gothic New, JetBrains Mono) are ready.
 * The Capture Agent should `await ensureFontsReady()` before exporting a PNG
 * so glyphs render with the final font rather than the system fallback.
 */
export async function ensureFontsReady(): Promise<void> {
  if (typeof document !== 'undefined' && 'fonts' in document) {
    await document.fonts.ready;
  }
}

/** Re-export the safe-zone constant for callers that want to mirror it. */
export const TYPOGRAPHY_SAFE_ZONE_PX = SAFE_ZONE_PX;

// ─── Offscreen 2D-canvas typography (for PNG export) ──────────────────────────

export interface RenderTypographyOpts {
  /** Output canvas width in pixels. */
  width: number;
  /** Output canvas height in pixels. */
  height: number;
  /** Layout from `pickCorners(seed, aspect)`. */
  layout: TextLayout;
  /** Title text. */
  title: string;
  /** Date string. */
  date: string;
  /** Time string. */
  time: string;
  /**
   * Optional override for title font-size in CSS pixels (live-overlay value).
   * Scaled to export resolution by the same shortEdge/1080 factor used for
   * the safe-zone, keeping the PNG visually consistent with the on-screen
   * preview at higher resolutions.
   */
  titleSizePx?: number;
  /** Optional title font-weight (100..900). */
  titleWeight?: number;
  /**
   * Title axis. 'vertical' stacks the title glyphs top-to-bottom in
   * a single column (mirroring the DOM overlay's writing-mode:
   * vertical-rl). Metadata always renders horizontally regardless.
   * Default: 'horizontal'.
   */
  orientation?: TitleOrientation;
}

interface DrawSpec {
  /** Single line, or array of lines (rendered top-to-bottom). */
  lines: string[];
  fontPx: number;
  fontFamily: string;
  fontWeight: number;
  letterSpacing: number;
  /** CSS-style line-height multiplier (line baseline-to-baseline = fontPx * lineHeight). */
  lineHeight: number;
  corner: Corner;
  /**
   * 'vertical' = stack glyphs top-to-bottom (single column). Title
   * uses this; metadata is always 'horizontal'. Default: 'horizontal'.
   */
  axis?: TitleOrientation;
}

const TEXT_COLOR = '#1A1A1A';

/**
 * Render the title/date/time typography onto a fresh 2D canvas at the
 * requested pixel size — the offscreen counterpart to `composeTypography`.
 * Used by the PNG export pipeline (`TypographyAdapter.renderToCanvas`).
 *
 * Sizing rule mirrors composeTypography():
 *   titleUnit = shortEdge / TITLE_UNIT_DIVISOR (=14)
 *   monoPx    = titleUnit * MONO_UNIT_RATIO (=0.18)
 * so PNG typography keeps the same visual proportions as the live overlay.
 *
 * Corner anchoring uses the SAFE_ZONE_PX safe-zone (32px) — the same the
 * CSS overlay uses — but scaled to the export resolution by mapping
 * 32 CSS px to 32 * (shortEdge / 1080) export px. This keeps the safe zone
 * proportionally constant against the canvas size.
 */
export async function renderTypographyToCanvas(
  opts: RenderTypographyOpts,
): Promise<HTMLCanvasElement> {
  await ensureFontsReady();

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.floor(opts.width));
  canvas.height = Math.max(1, Math.floor(opts.height));
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('renderTypographyToCanvas: failed to acquire 2D context');
  }

  const shortEdge = Math.min(canvas.width, canvas.height);
  const autoTitlePx = shortEdge / TITLE_UNIT_DIVISOR;
  // When the live overlay supplies an explicit titleSizePx (CSS px against a
  // 1080-tall canvas convention), scale it to the export resolution by the
  // same factor used for the safe-zone so PNG/screen stay proportional.
  const titlePx =
    typeof opts.titleSizePx === 'number' && opts.titleSizePx > 0
      ? (opts.titleSizePx * shortEdge) / 1080
      : autoTitlePx;
  // Mono captions still derive from the AUTO unit so they don't balloon when
  // the title is bumped up; this preserves the relative-rhythm of the layout.
  const monoPx = autoTitlePx * MONO_UNIT_RATIO;

  // Scale the safe-zone proportionally — 32px against a 1080-tall canvas, so
  // a 4K export gets a 64px safe-zone (proportional, not absolute). This
  // keeps the optical rhythm consistent across resolutions.
  const safeZone = (SAFE_ZONE_PX * shortEdge) / 1080;

  const orientation: TitleOrientation = opts.orientation ?? 'horizontal';
  // Match the CSS letter-spacing values from typography.css (single
  // source of truth = the LETTER_SPACING_TITLE_*_EM constants above).
  const titleLetterSpacingEm =
    orientation === 'vertical'
      ? LETTER_SPACING_TITLE_VERTICAL_EM
      : LETTER_SPACING_TITLE_HORIZONTAL_EM;

  // Title may contain `\n` line breaks authored from the textarea
  // input. Horizontal orientation splits into per-line draws (the
  // generic drawText already lays multiple lines on baseline-step
  // multiples). Vertical orientation collapses breaks to a single
  // space so the column stays a single run — the existing
  // drawVerticalText only handles a single column today.
  const titleLines = orientation === 'vertical'
    ? [opts.title.replace(/\n/g, ' ')]
    : opts.title.split('\n');

  const drawSpecs: DrawSpec[] = [
    {
      lines: titleLines,
      fontPx: titlePx,
      fontFamily: '"Zen Kaku Gothic New", sans-serif',
      fontWeight:
        typeof opts.titleWeight === 'number' && Number.isFinite(opts.titleWeight)
          ? Math.round(opts.titleWeight)
          : 500,
      letterSpacing: titleLetterSpacingEm * titlePx,
      // 1.0 packs lines tightly enough that consecutive `\n` rows
      // touch each other vertically; 1.2 gives just enough air to
      // read as separate lines without bloating the layout. 要調整
      // — adjust once the visual director sees a real two-line
      // composition on screen.
      lineHeight: 1.2,
      corner: opts.layout.title.corner,
      axis: orientation,
    },
    {
      // Metadata: date on line 1, time on line 2 (Phase 6).
      lines: [opts.date, opts.time],
      fontPx: monoPx,
      fontFamily: '"JetBrains Mono", ui-monospace, monospace',
      fontWeight: 400,
      letterSpacing: 0.03 * monoPx,
      lineHeight: 1.6,
      corner: opts.layout.metadata.corner,
      axis: 'horizontal',
    },
  ];

  ctx.fillStyle = TEXT_COLOR;
  ctx.textBaseline = 'top';

  for (const spec of drawSpecs) {
    if (spec.axis === 'vertical') {
      drawVerticalText(ctx, spec, canvas.width, canvas.height, safeZone);
    } else {
      drawText(ctx, spec, canvas.width, canvas.height, safeZone);
    }
  }

  return canvas;
}

/**
 * Draw a single text element with corner anchoring and optional letter
 * spacing. We implement letter spacing manually (per-glyph) because
 * CanvasRenderingContext2D.letterSpacing has uneven browser support.
 */
function drawText(
  ctx: CanvasRenderingContext2D,
  spec: DrawSpec,
  canvasW: number,
  canvasH: number,
  safeZone: number,
): void {
  ctx.font = `${spec.fontWeight} ${spec.fontPx}px ${spec.fontFamily}`;

  const isRight = spec.corner === 'tr' || spec.corner === 'br';
  const isBottom = spec.corner === 'bl' || spec.corner === 'br';

  ctx.textAlign = isRight ? 'right' : 'left';

  const lineStep = spec.fontPx * spec.lineHeight;
  const blockHeight = spec.fontPx + lineStep * (spec.lines.length - 1);

  const x = isRight ? canvasW - safeZone : safeZone;
  // For top corners: start at safeZone. For bottom corners: align the LAST
  // line's baseline to (canvasH - safeZone - fontPx) so the block as a whole
  // sits inside the safe zone.
  const yTop = isBottom
    ? canvasH - safeZone - blockHeight
    : safeZone;

  for (let i = 0; i < spec.lines.length; i++) {
    const line = spec.lines[i] ?? '';
    const yLine = yTop + i * lineStep;
    drawLine(ctx, line, spec.letterSpacing, x, yLine, isRight);
  }
}

function drawLine(
  ctx: CanvasRenderingContext2D,
  text: string,
  letterSpacing: number,
  x: number,
  y: number,
  isRight: boolean,
): void {
  if (Math.abs(letterSpacing) < 0.05) {
    ctx.fillText(text, x, y);
    return;
  }
  if (isRight) {
    const widths: number[] = [];
    let total = 0;
    for (let i = 0; i < text.length; i++) {
      const ch = text.charAt(i);
      const w = ctx.measureText(ch).width;
      widths.push(w);
      total += w + (i < text.length - 1 ? letterSpacing : 0);
    }
    let cur = x - total;
    ctx.textAlign = 'left';
    for (let i = 0; i < text.length; i++) {
      const ch = text.charAt(i);
      ctx.fillText(ch, cur, y);
      cur += (widths[i] ?? 0) + letterSpacing;
    }
    ctx.textAlign = 'right';
    return;
  }
  let cur = x;
  for (let i = 0; i < text.length; i++) {
    const ch = text.charAt(i);
    ctx.fillText(ch, cur, y);
    cur += ctx.measureText(ch).width + letterSpacing;
  }
}

/**
 * Draw a vertical (top-to-bottom) title column. Mirrors the DOM
 * overlay's `writing-mode: vertical-rl; text-orientation: upright`
 * behaviour for Japanese-first titles.
 *
 * Glyph stacking:
 *   - Iterate via `Array.from(text)` so surrogate-pair codepoints
 *     (rare emoji etc.) and combining marks are treated as one
 *     unit each. `String.prototype.charAt` would split them.
 *   - Each glyph is centred horizontally on the column axis (column
 *     centre = anchor x). The vertical advance is one fontPx box +
 *     `letterSpacing`, matching the CSS letter-spacing convention
 *     (added between, not before, glyphs).
 *
 * Corner anchoring (writing-mode: vertical-rl convention):
 *   - tr / br: column anchor x at `canvasW - safeZone - fontPx/2`
 *     (right-aligned). tl / bl: anchor x at `safeZone + fontPx/2`.
 *   - tl / tr: first glyph top at `safeZone`. bl / br: last glyph
 *     bottom at `canvasH - safeZone` (= block bottom-aligned in safe-
 *     zone). 要調整 — when the column is taller than the safe area
 *     this still overflows; we accept that for now (column-count
 *     fallback / multi-column wrap is a follow-up).
 */
function drawVerticalText(
  ctx: CanvasRenderingContext2D,
  spec: DrawSpec,
  canvasW: number,
  canvasH: number,
  safeZone: number,
): void {
  ctx.font = `${spec.fontWeight} ${spec.fontPx}px ${spec.fontFamily}`;

  // Vertical title currently stacks the first line only; metadata is
  // never vertical so multi-line specs aren't expected here.
  const text = spec.lines[0] ?? '';
  const glyphs = Array.from(text);
  if (glyphs.length === 0) return;

  const isRight = spec.corner === 'tr' || spec.corner === 'br';
  const isBottom = spec.corner === 'bl' || spec.corner === 'br';

  // Column centre on the X axis. The column is one fontPx wide
  // (we approximate the upright-CJK em-box as a square).
  const columnX = isRight
    ? canvasW - safeZone - spec.fontPx / 2
    : safeZone + spec.fontPx / 2;

  const stepY = spec.fontPx + spec.letterSpacing;
  // Total column height = fontPx + (n-1) × step (n glyphs, n-1 gaps).
  const blockH = spec.fontPx + (glyphs.length - 1) * stepY;
  const yTop = isBottom ? canvasH - safeZone - blockH : safeZone;

  // Centre each glyph on the column axis using textAlign='center'.
  const prevAlign = ctx.textAlign;
  ctx.textAlign = 'center';
  for (let i = 0; i < glyphs.length; i++) {
    const ch = glyphs[i] ?? '';
    const y = yTop + i * stepY;
    ctx.fillText(ch, columnX, y);
  }
  ctx.textAlign = prevAlign;
}
